import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const TALLY_LABELS = [
  ['created', 'Ingested'],
  ['already_present', 'Already present'],
  ['thread_duplicate', 'Thread duplicate'],
  ['content_duplicate', 'Content duplicate'],
  ['statement_routed', 'Routed to statements'],
  ['deterministic_rejected', 'Deterministic reject'],
  ['llm_rejected', 'Not an invoice (AI)'],
  ['fetch_failed', 'Fetch failed'],
  ['rate_limited', 'Rate limited'],
  ['error', 'Error'],
];

// Buckets worth surfacing in the "what did it skip and why" disclosure --
// success/dup outcomes aren't skips, so they're left out.
const SKIP_BUCKETS = ['fetch_failed', 'deterministic_rejected', 'llm_rejected', 'rate_limited', 'error'];

function fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return null;
  }
}

function isFuture(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) && t > Date.now();
}

// Mirrors ManagementStatementBackfillPanel.jsx's useJobPolling.
function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.gmailInvoiceBackfillJobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, start };
}

export function GmailInvoiceBackfillPanel() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sender, setSender] = useState('');
  const [chain, setChain] = useState(null);
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [showSkips, setShowSkips] = useState(false);

  const fetchChain = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.gmailInvoiceBackfillStatus();
      setChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const poll = useJobPolling({
    onCompleted: useCallback((result) => {
      setProgress(null);
      fetchChain();
      toast.success(result?.truncated ? 'Run complete -- chain continuing' : 'Backfill complete');
    }, [fetchChain]),
    onFailed: useCallback((error, lastProgress) => {
      setProgress((prev) => lastProgress ?? prev);
      // Not stored locally for display -- fetchChain() below pulls the
      // chain-doc-authoritative last_run_error, which a NEW run correctly
      // clears server-side. Local state here would otherwise outlive the
      // run it belongs to (the original "stale error under a running
      // chain" bug).
      fetchChain();
      toast.error(error ?? 'Backfill run failed');
    }, [fetchChain]),
    onProgress: useCallback((p) => setProgress(p), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.gmailInvoiceBackfillJobLatest();
        const job = data?.job;
        if (job?.status === 'running') {
          setProgress(job.progress || null);
          poll.start(job._id);
        } else if (job?.status === 'failed') {
          setProgress(job.progress || null);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchChain(); }, [fetchChain]);

  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChain]);

  const runBackfill = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Pick both a start and end date first.');
      return;
    }
    setStarting(true);
    try {
      const { data } = await diagnosticsAPI.gmailInvoiceBackfillStart(dateFrom, dateTo, sender.trim() || null);
      if (data.job_id) {
        poll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A backfill chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const stopChain = async () => {
    if (!chain?._id) return;
    if (!window.confirm('Stop the running backfill chain? The current run finishes its time window normally.')) return;
    setStopping(true);
    try {
      await diagnosticsAPI.gmailInvoiceBackfillStop(chain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStopping(false);
    }
  };

  const running = chain?.status === 'running';
  // progress (this tab's own job poll) > chain.progress (live, any poller,
  // updated every few messages -- services.chunked_chain.ChainStore.
  // update_progress) > chain.seed (only refreshed at run END). Falling
  // back straight to chain.seed used to be the only option and is exactly
  // why a fresh page load mid-run-2 rendered "-/375": chain.seed still
  // held run 1's final state and nothing newer was available cross-tab.
  const live = progress || chain?.progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const tallies = live?.tallies || {};
  // last_run_error is chain-doc-authoritative and cleared the instant a
  // NEW run starts (services.chunked_chain.ChainStore.record_run_start) --
  // this is what makes the error line honest under a chain whose status
  // is "running" again. `failed` (local state) is no longer used for
  // display, only for the immediate toast in onFailed.
  const errorText = (chain?.status === 'error' || chain?.status === 'dead_letter')
    ? chain?.error
    : chain?.last_run_error;
  const lastActivity = fmtTime(chain?.heartbeat_at);
  const rateLimitedUntil = live?.rate_limited_until;
  const rateLimited = isFuture(rateLimitedUntil);
  const skipSamples = SKIP_BUCKETS
    .map((key) => [key, live?.samples?.[key] || []])
    .filter(([, entries]) => entries.length > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Gmail Invoice Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          One-time catch-up scan over a chosen date range (optionally narrowed by
          sender), through the same ingestion pipeline the recurring scheduled scan
          uses -- dedup, quarantine, and the FinanceAI/Filed label all apply
          identically, so a re-run over an overlapping range costs nothing.
          Budget-boxed: a wide range runs across several self-scheduled passes.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="gib-date-from">From</label>
            <Input
              id="gib-date-from" type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="gmail-invoice-backfill-date-from"
              className="h-8 w-36"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="gib-date-to">To</label>
            <Input
              id="gib-date-to" type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="gmail-invoice-backfill-date-to"
              className="h-8 w-36"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="gib-sender">Sender (optional)</label>
            <Input
              id="gib-sender" type="text" placeholder="supplier@example.com" value={sender}
              onChange={(e) => setSender(e.target.value)}
              data-testid="gmail-invoice-backfill-sender"
              className="h-8 w-48"
            />
          </div>
          <Button
            data-testid="gmail-invoice-backfill-run"
            onClick={runBackfill}
            disabled={starting || poll.polling || running}
            size="sm"
          >
            {(starting || poll.polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
            {running
              ? rateLimited
                ? `Chain running, run ${fmtInt(chain.runs_count)}, rate-limited -- resumes ~${fmtTime(rateLimitedUntil)}`
                : `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
              : 'Run Backfill'}
          </Button>
          {running && (
            <Button data-testid="gmail-invoice-backfill-stop" onClick={stopChain} disabled={stopping} size="sm" variant="outline">
              {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Stop Chain
            </Button>
          )}
          {poll.polling && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              running...
            </span>
          )}
        </div>

        {chain && (
          <div className="text-xs space-y-1" data-testid="gmail-invoice-backfill-chain-summary">
            <div className="flex items-center gap-1 flex-wrap">
              <Badge variant={running ? 'default' : (chain.status === 'error' || chain.status === 'dead_letter') ? 'destructive' : 'outline'}>
                {chain.status}
              </Badge>
              <span className="text-muted-foreground">
                run {fmtInt(chain.runs_count)}
                {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
              </span>
              {running && lastActivity && (
                <span className="text-muted-foreground" data-testid="gmail-invoice-backfill-last-activity">
                  -- last activity {lastActivity}
                </span>
              )}
            </div>
            {rateLimited && (
              <p className="text-amber-600 dark:text-amber-400" data-testid="gmail-invoice-backfill-rate-limited">
                Rate-limited by the AI parser -- run paused, resumes automatically around {fmtTime(rateLimitedUntil)}.
              </p>
            )}
            {Object.keys(tallies).length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="gmail-invoice-backfill-tallies">
                {TALLY_LABELS.map(([key, label]) => {
                  const count = tallies[key];
                  if (!count) return null;
                  return <span key={key}>{label}: <strong>{count}</strong></span>;
                })}
              </div>
            )}
            {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}
            {skipSamples.length > 0 && (
              <div>
                <button
                  type="button"
                  className="text-muted-foreground underline"
                  data-testid="gmail-invoice-backfill-skips-toggle"
                  onClick={() => setShowSkips((v) => !v)}
                >
                  {showSkips ? 'Hide' : 'Show'} recent skips/errors
                </button>
                {showSkips && (
                  <ul className="mt-1 space-y-0.5" data-testid="gmail-invoice-backfill-skips-list">
                    {skipSamples.flatMap(([outcome, entries]) => entries.map((entry, i) => (
                      <li key={`${outcome}-${i}`} className="text-muted-foreground">
                        <span className="font-mono">{entry.msg_id || '(unknown)'}</span>
                        {' -- '}{outcome}
                        {entry.reason ? `: ${entry.reason}` : ''}
                      </li>
                    )))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
